import type { DestinationStream } from "pino";

export const isServer = typeof window === "undefined";

export const isNextEdgeRuntime = process.env.NEXT_RUNTIME === "edge";

/**
 * True when running in a Node.js server context (Next.js nodejs runtime,
 * standalone ECS services, scripts, etc.). False in browsers and edge runtimes
 * where Node APIs like process.stdout are unavailable.
 */
export const isNodeServer = isServer && process.env.NEXT_RUNTIME !== "edge";

export const isLocalScript =
  process.env.NEXT_RUNTIME === undefined &&
  typeof window === "undefined" &&
  process.env.LOCAL_SCRIPT === "true";

/**
 * Returns process.stdout via an indirect computed key so bundlers targeting
 * the Edge runtime don't statically flag `process.stdout` (a Node-only API)
 * in modules that are also reachable from Edge code (middleware,
 * instrumentation). Only ever called on the Node.js server.
 */
export const getStdoutStream = (): DestinationStream => {
  const stdoutKey = "stdout";
  return (process as any)[stdoutKey];
};

/**
 * Datadog derives a log's severity from the first reserved attribute it finds,
 * and it checks `status` before `level`. Values are then mapped by their first
 * letter: "a" is alert, "c" critical, "f" emergency, "o"/"s" ok, anything
 * unrecognized info. So `status: "account_found"` on an info line paged as
 * ALERT, and `status: 404` on a logger.error line was demoted to info. Moving
 * the value to `appStatus` keeps it searchable (`@appStatus`) without letting
 * it touch the severity.
 */
export const renameReservedLogKeys = (
  logObject: Record<string, unknown>,
): Record<string, unknown> => {
  if (!Object.prototype.hasOwnProperty.call(logObject, "status")) {
    return logObject;
  }
  const { status, ...rest } = logObject;
  return { appStatus: status, ...rest };
};

export const createDatadogLocalTransport = (): DestinationStream | null => {
  const ddApiKey = process.env.DD_API_KEY;
  const ddService =
    process.env.DD_SERVICE || process.env.NEXT_PUBLIC_DD_SERVICE;
  const ddEnv = process.env.NEXT_PUBLIC_DD_ENV;
  const ddSite = "us5.datadoghq.com";

  const isDev = process.env.NODE_ENV === "development";

  if (!ddApiKey || !isNodeServer || isLocalScript || isDev) {
    if (!ddApiKey && isNodeServer && !isLocalScript && !isDev) {
      if (!(global as any).__datadogTransportWarningLogged) {
        // eslint-disable-next-line no-console
        console.warn(
          "[Datadog Logger] DD_API_KEY not set. Logs will not be sent to Datadog. " +
            "To enable Datadog logging locally, add DD_API_KEY to your .env.local file.",
        );
        (global as any).__datadogTransportWarningLogged = true;
      }
    }

    return null;
  }

  let logBuffer: any[] = [];
  let flushTimeout: NodeJS.Timeout | null = null;

  const flushLogs = async () => {
    if (logBuffer.length === 0) {
      return;
    }

    const logsToSend = [...logBuffer];
    logBuffer = [];
    flushTimeout = null;

    try {
      const response = await fetch(
        `https://http-intake.logs.${ddSite}/api/v2/logs`,
        {
          method: "POST",
          headers: {
            "DD-API-KEY": ddApiKey,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(
            logsToSend.map((logObj) => {
              const level = logObj.level;
              const status =
                level >= 50 ? "error" : level >= 40 ? "warn" : "info";

              return {
                message: logObj.msg || JSON.stringify(logObj),
                ddtags: `env:${ddEnv || "local"},service:${ddService || "marble"}`,
                ddsource: "nodejs",
                hostname: logObj.hostname || "local",
                service: ddService || "marble",
                status,
                timestamp: logObj.time
                  ? new Date(logObj.time).getTime()
                  : Date.now(),
                ...logObj,
              };
            }),
          ),
        },
      );

      if (!response.ok) {
        // eslint-disable-next-line no-console
        console.error(
          `[Datadog] Failed to send logs: ${response.status} ${response.statusText}`,
        );
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("[Datadog] Error sending logs:", error);
    }
  };

  return {
    write(message: string) {
      try {
        const logObj = JSON.parse(message);
        logBuffer.push(logObj);

        if (logBuffer.length >= 10) {
          if (flushTimeout) {
            clearTimeout(flushTimeout);
            flushTimeout = null;
          }
          flushLogs();
        } else if (!flushTimeout) {
          flushTimeout = setTimeout(flushLogs, 1000);
        }
      } catch (error) {
        // stdout stream will handle it
      }
    },
  } as DestinationStream;
};
